import React from "react";
import ExcursionCardTooltip from "components/shared/excursionModal/display/ExcursionCardTooltip";
import type { Excursion, ExcursionPlanningCardContext } from "api/excursions/Excursion.typed";

export type ExcursionPlanningCardPopoverProps = ExcursionPlanningCardContext & {
  excursion: Excursion;
  /** Chip-drag sources are only allowed from the side drawer (`ExcursionPlanningGroupsDrawer`). */
  planningChipDragTooltipTargetId: number | null;
  children: React.ReactElement;
};

/**
 * Rich Popper over planning excursion cards (groups + drops). Mirrors booking grid tooltips:
 * driven by hover/click native to ExcursionCardTooltip; `forceOpen` while chip-drag targets this card.
 */
export default function ExcursionPlanningCardPopover({
  excursion,
  children,
  planningChipDragTooltipTargetId,
  canEdit,
  canOpenExcursionDrawer,
  getGroupLabels,
  makeGroupChipDropHandlers,
  openPlanningGroupsDrawer,
  tooltipForceClose,
  clientChipDragInProgress,
  excursionTemplatesList,
  bookingStatusMap,
  getCustomerInProgressTrainingsTooltip,
  dismissPlanningForcedPopoverTarget,
  chipDropPending,
}: ExcursionPlanningCardPopoverProps) {
  /** Keeps Popper open after drop until tooltip `mouseleave` + dismiss; independent of chip-drag lifecycle. */
  const chipForcedPopoverThisExcursion =
    planningChipDragTooltipTargetId != null && planningChipDragTooltipTargetId === excursion.id;

  const destinationDragPopover = chipForcedPopoverThisExcursion;

  const planningPassthroughPopoverWhileChipDragElsewhere =
    clientChipDragInProgress && !chipForcedPopoverThisExcursion;

  return (
    <ExcursionCardTooltip
      excursion={excursion}
      canEdit={canEdit}
      groupLabels={getGroupLabels(excursion)}
      makeGroupChipDropHandlers={makeGroupChipDropHandlers}
      forceOpen={destinationDragPopover}
      forceClose={tooltipForceClose}
      planningClientChipDragActive={planningPassthroughPopoverWhileChipDragElsewhere}
      suppressHoverOpen={Boolean(clientChipDragInProgress)}
      clientChipDragSourceEnabled={false}
      onTooltipClick={
        canOpenExcursionDrawer
          ? (event: React.MouseEvent) => {
              event.stopPropagation();
              openPlanningGroupsDrawer(excursion.id);
            }
          : undefined
      }
      excursionTemplatesList={excursionTemplatesList}
      bookingStatusMap={bookingStatusMap?.[excursion.id]}
      getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
      onPlanningForcedPopoverDismiss={dismissPlanningForcedPopoverTarget}
      chipDropPending={chipDropPending?.excursionId === excursion.id ? chipDropPending : null}
    >
      {children}
    </ExcursionCardTooltip>
  );
}
